import { useIntl } from 'react-intl';

import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { purple } from '@mui/material/colors';

import type { PublisherAssignmentCount, UnassignedApplicant } from './utils';
import { getPublisherDisplayName } from './utils';
import messages from './messages';

export type SidebarView = 'assigned' | 'applied';

type PublisherSidebarProps = {
  view: SidebarView;
  multipleAssignments: PublisherAssignmentCount[];
  unassignedApplicants: UnassignedApplicant[];
  selectedPublisherId: number | null;
  onPublisherClick: (publisherId: number) => void;
  onViewChange: (view: SidebarView) => void;
};

const PublisherSidebar = ({
  view,
  multipleAssignments,
  unassignedApplicants,
  selectedPublisherId,
  onPublisherClick,
  onViewChange,
}: PublisherSidebarProps) => {
  const intl = useIntl();

  const hasMultiple = multipleAssignments.length > 0;
  const hasUnassigned = unassignedApplicants.length > 0;
  if (!hasMultiple && !hasUnassigned) return null;

  const showToggle = hasMultiple && hasUnassigned;
  const activeView: SidebarView = hasMultiple ? view : 'applied';

  const items =
    activeView === 'applied'
      ? unassignedApplicants.map((p) => ({
          publisherId: p.publisherId,
          firstName: p.firstName,
          lastName: p.lastName,
          secondary: intl.formatMessage(messages.applicationCount, {
            count: p.appliedShiftIds.length,
          }),
        }))
      : multipleAssignments.map((p) => ({
          publisherId: p.publisherId,
          firstName: p.firstName,
          lastName: p.lastName,
          secondary: intl.formatMessage(messages.assignmentCount, {
            count: p.count,
          }),
        }));

  return (
    <Box
      sx={{
        borderRight: 1,
        borderColor: 'divider',
        height: '100%',
      }}
    >
      {showToggle ? (
        <Box sx={{ px: 2, pt: 1, pb: 1 }}>
          <ToggleButtonGroup
            value={activeView}
            exclusive
            size="small"
            fullWidth
            onChange={(_, next: SidebarView | null) => {
              if (next != null) onViewChange(next);
            }}
          >
            <ToggleButton value="assigned">
              {intl.formatMessage(messages.multipleAssignments)}
            </ToggleButton>
            <ToggleButton value="applied">
              {intl.formatMessage(messages.unassignedApplicants)}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      ) : (
        <Typography
          variant="overline"
          sx={{ fontWeight: 'bold', display: 'block', px: 2, pt: 1, mb: 1 }}
        >
          {intl.formatMessage(
            activeView === 'applied'
              ? messages.unassignedApplicants
              : messages.multipleAssignments
          )}
        </Typography>
      )}
      <List disablePadding dense>
        {items.map((item) => (
          <ListItemButton
            key={item.publisherId}
            selected={selectedPublisherId === item.publisherId}
            onClick={() => onPublisherClick(item.publisherId)}
            sx={{
              '&.Mui-selected': {
                bgcolor: purple[50],
                color: purple[900],
                '&:hover': { bgcolor: purple[100] },
              },
            }}
          >
            <ListItemText
              primary={getPublisherDisplayName(item)}
              secondary={item.secondary}
            />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );
};

export default PublisherSidebar;
